import React, { useState, useEffect } from 'react'
import { X, MessageSquare, Share2, Brain, Sliders, Download, Trash2, Copy, Eye, ChevronRight, Save, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import { outputLengthConfig } from './WorldSimulatorSettings'

/**
 * 世界体验对话设置对话框
 * 包含：聊天记录、分享作品、记忆设定、模型参数
 */
export const ExperienceSettingsDialog = ({ 
  isOpen, 
  onClose,
  messages = [],
  memorySummary = '',
  worldData = null,
  sessionId = '',
  onClearHistory,
  onExportHistory,
  onRefreshMemory
}) => {
  const [activeTab, setActiveTab] = useState('chat') // 'chat', 'share', 'memory', 'model'
  
  // 输出长度选项数组（用于滑块）
  const outputLengthOptions = Object.entries(outputLengthConfig).map(([key, config]) => ({
    key,
    ...config
  }))
  
  // 获取输出长度的索引
  const getOutputLengthIndex = (key) => {
    const index = outputLengthOptions.findIndex(opt => opt.key === key)
    return index >= 0 ? index : 1 // 默认返回 medium 的索引
  }
  
  // 根据索引获取输出长度键
  const getOutputLengthKey = (index) => {
    return outputLengthOptions[index]?.key || 'medium'
  }
  
  const [modelSettings, setModelSettings] = useState({
    outputLength: 'medium',
    temperature: 0.8,
    topP: 0.9,
    memoryFrequency: 10, // 每N条消息生成一次记忆
    maxHistory: 20, // 保留的对话历史数量
    autoSaveMemory: true
  })

  // 加载模型设置
  useEffect(() => {
    if (isOpen) {
      const saved = localStorage.getItem('experienceModelSettings')
      if (saved) {
        try {
          setModelSettings({ ...modelSettings, ...JSON.parse(saved) })
        } catch (error) {
          console.error('加载设置失败:', error)
        }
      }
    }
  }, [isOpen])

  // 保存模型设置
  const handleSaveModelSettings = () => {
    try {
      localStorage.setItem('experienceModelSettings', JSON.stringify(modelSettings))
      toast.success('模型参数已保存')
    } catch (error) {
      console.error('保存设置失败:', error)
      toast.error('保存失败')
    }
  }

  // 导出聊天记录
  const handleExportChat = () => {
    try {
      const exportData = {
        worldId: worldData?.worldId || worldData?.id,
        worldName: worldData?.basicInfo?.worldName || '未知世界',
        sessionId,
        exportTime: new Date().toISOString(),
        messages: messages.map(m => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp
        })),
        messageCount: messages.length
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `chat-${worldData?.basicInfo?.worldName || 'world'}-${new Date().getTime()}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      toast.success('聊天记录已导出')
      if (onExportHistory) onExportHistory()
    } catch (error) {
      console.error('导出失败:', error)
      toast.error('导出失败')
    }
  }

  // 导出为文本格式
  const handleExportText = () => {
    try {
      let text = `【${worldData?.basicInfo?.worldName || '未知世界'}】对话记录\n`
      text += `导出时间：${new Date().toLocaleString()}\n`
      text += `会话ID：${sessionId}\n`
      text += `消息数量：${messages.length}\n`
      text += `\n${'='.repeat(60)}\n\n`

      messages.forEach((msg, idx) => {
        const roleLabel = msg.role === 'user' ? '👤 玩家' : '🤖 AI'
        const time = msg.timestamp ? new Date(msg.timestamp).toLocaleString() : ''
        text += `${roleLabel} ${time}\n${msg.content}\n\n${'-'.repeat(60)}\n\n`
      })

      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `chat-${worldData?.basicInfo?.worldName || 'world'}-${new Date().getTime()}.txt`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      toast.success('文本记录已导出')
    } catch (error) {
      console.error('导出失败:', error)
      toast.error('导出失败')
    }
  }

  // 复制分享链接
  const handleCopyShareLink = () => {
    const shareUrl = `${window.location.origin}/content/world/${worldData?.worldId || worldData?.id}`
    navigator.clipboard.writeText(shareUrl)
      .then(() => toast.success('分享链接已复制'))
      .catch(() => toast.error('复制失败'))
  }

  // 清空聊天记录
  const handleClearChat = () => {
    if (window.confirm('确定要清空所有聊天记录吗？此操作不可恢复。')) {
      if (onClearHistory) onClearHistory()
      toast.success('聊天记录已清空')
    }
  }

  // 刷新记忆摘要
  const handleRefreshMemorySummary = () => {
    if (onRefreshMemory) {
      onRefreshMemory()
      toast.success('正在重新生成记忆摘要...')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[101] p-4">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-purple-500/20">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <Sliders className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">体验设置</h2>
              <p className="text-sm text-gray-400">管理聊天记录、记忆和模型参数</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 标签页导航 */}
        <div className="flex border-b border-gray-700/50 px-6">
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'chat'
                ? 'text-purple-400 border-purple-400'
                : 'text-gray-400 border-transparent hover:text-gray-300'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            聊天记录
          </button>
          <button
            onClick={() => setActiveTab('share')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'share'
                ? 'text-purple-400 border-purple-400'
                : 'text-gray-400 border-transparent hover:text-gray-300'
            }`}
          >
            <Share2 className="w-4 h-4" />
            分享作品
          </button>
          <button
            onClick={() => setActiveTab('memory')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'memory'
                ? 'text-purple-400 border-purple-400'
                : 'text-gray-400 border-transparent hover:text-gray-300'
            }`}
          >
            <Brain className="w-4 h-4" />
            记忆设定
          </button>
          <button
            onClick={() => setActiveTab('model')}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'model'
                ? 'text-purple-400 border-purple-400'
                : 'text-gray-400 border-transparent hover:text-gray-300'
            }`}
          >
            <Sliders className="w-4 h-4" />
            模型参数
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* 聊天记录标签页 */}
          {activeTab === 'chat' && (
            <div className="space-y-6">
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">对话统计</h3>
                    <p className="text-sm text-gray-400 mt-1">当前会话的对话记录信息</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/20">
                    <div className="text-2xl font-bold text-purple-400">{messages.length}</div>
                    <div className="text-sm text-gray-400 mt-1">总消息数</div>
                  </div>
                  <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
                    <div className="text-2xl font-bold text-blue-400">
                      {messages.filter(m => m.role === 'user').length}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">玩家消息</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-white mb-4">历史记录</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {messages.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">暂无聊天记录</div>
                  ) : (
                    messages.slice(-10).reverse().map((msg, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-700/30 rounded-lg p-3 text-sm"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-medium ${
                            msg.role === 'user' ? 'text-blue-400' : 'text-purple-400'
                          }`}>
                            {msg.role === 'user' ? '👤 玩家' : '🤖 AI'}
                          </span>
                          <span className="text-gray-500 text-xs">
                            {msg.timestamp ? new Date(msg.timestamp).toLocaleString() : ''}
                          </span>
                        </div>
                        <div className="text-gray-300 line-clamp-2">{msg.content}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleExportChat}
                  disabled={messages.length === 0}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  导出为 JSON
                </button>
                <button
                  onClick={handleExportText}
                  disabled={messages.length === 0}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  导出为 TXT
                </button>
                <button
                  onClick={handleClearChat}
                  disabled={messages.length === 0}
                  className="w-full flex items-center justify-center gap-2 bg-red-600 text-white py-3 rounded-xl hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  清空聊天记录
                </button>
              </div>
            </div>
          )}

          {/* 分享作品标签页 */}
          {activeTab === 'share' && (
            <div className="space-y-6">
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-white mb-4">分享世界</h3>
                <p className="text-sm text-gray-400 mb-4">
                  分享这个精彩的世界给其他玩家体验
                </p>
                <button
                  onClick={handleCopyShareLink}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all"
                >
                  <Copy className="w-4 h-4" />
                  复制分享链接
                </button>
              </div>

              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-white mb-4">导出体验记录</h3>
                <p className="text-sm text-gray-400 mb-4">
                  导出你的游玩记录，包含完整的对话内容
                </p>
                <div className="space-y-3">
                  <button
                    onClick={handleExportChat}
                    disabled={messages.length === 0}
                    className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white py-3 rounded-xl hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    导出完整记录 (JSON)
                  </button>
                  <button
                    onClick={handleExportText}
                    disabled={messages.length === 0}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    导出对话文本 (TXT)
                  </button>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-4 border border-purple-500/20">
                <div className="flex items-start gap-3">
                  <Eye className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-1">分享提示</h4>
                    <p className="text-sm text-gray-400">
                      导出的记录可以分享到社区或保存为纪念。其他玩家可以通过分享链接直接体验这个世界。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 记忆设定标签页 */}
          {activeTab === 'memory' && (
            <div className="space-y-6">
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">当前记忆摘要</h3>
                    <p className="text-sm text-gray-400 mt-1">AI 对当前会话的记忆总结</p>
                  </div>
                  <button
                    onClick={handleRefreshMemorySummary}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors text-purple-400 hover:text-purple-300"
                    title="重新生成记忆"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4 min-h-[120px] max-h-[300px] overflow-y-auto">
                  {memorySummary ? (
                    <p className="text-sm text-gray-300 whitespace-pre-wrap">{memorySummary}</p>
                  ) : (
                    <p className="text-sm text-gray-500 italic">暂无记忆摘要，继续对话后将自动生成</p>
                  )}
                </div>
              </div>

              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-white mb-4">记忆设置</h3>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-300">记忆生成频率</span>
                      <span className="text-sm text-purple-400">{modelSettings.memoryFrequency} 条消息</span>
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="20"
                      step="5"
                      value={modelSettings.memoryFrequency}
                      onChange={(e) => setModelSettings({ ...modelSettings, memoryFrequency: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">每隔多少条消息自动生成一次记忆摘要</p>
                  </div>

                  <div>
                    <label className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-300">保留历史数量</span>
                      <span className="text-sm text-purple-400">{modelSettings.maxHistory} 条</span>
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="50"
                      step="10"
                      value={modelSettings.maxHistory}
                      onChange={(e) => setModelSettings({ ...modelSettings, maxHistory: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">AI 能记住的最近对话数量</p>
                  </div>

                  <div className="flex items-center justify-between py-2">
                    <div>
                      <div className="text-sm font-medium text-gray-300">自动保存记忆</div>
                      <p className="text-xs text-gray-500 mt-1">自动将记忆摘要保存到会话</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={modelSettings.autoSaveMemory}
                        onChange={(e) => setModelSettings({ ...modelSettings, autoSaveMemory: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl p-4 border border-blue-500/20">
                <div className="flex items-start gap-3">
                  <Brain className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-1">关于记忆系统</h4>
                    <p className="text-sm text-gray-400">
                      AI 会定期总结对话内容，形成记忆摘要。这有助于在长时间对话中保持连贯性，记住重要的情节和角色关系。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 模型参数标签页 */}
          {activeTab === 'model' && (
            <div className="space-y-6">
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-white mb-4">输出设置</h3>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-300">输出长度</span>
                      <span className="text-sm text-purple-400">
                        {outputLengthConfig[modelSettings.outputLength]?.label || '中等'}
                      </span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max={outputLengthOptions.length - 1}
                      step="1"
                      value={getOutputLengthIndex(modelSettings.outputLength)}
                      onChange={(e) => {
                        const index = parseInt(e.target.value)
                        const newKey = getOutputLengthKey(index)
                        setModelSettings({ ...modelSettings, outputLength: newKey })
                      }}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    />
                    <div className="flex justify-between mt-2 px-1">
                      {outputLengthOptions.map((opt, idx) => (
                        <div
                          key={opt.key}
                          className={`text-xs text-center ${
                            getOutputLengthIndex(modelSettings.outputLength) === idx
                              ? 'text-purple-400 font-medium'
                              : 'text-gray-500'
                          }`}
                        >
                          <div className="mb-1">
                            {opt.key === 'short' ? '短' : opt.key === 'medium' ? '中' : opt.key === 'long' ? '长' : '很长'}
                          </div>
                          <div className="text-[10px] text-gray-600">
                            {opt.min}-{opt.max}字
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">控制 AI 生成文本的长度</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-white mb-4">生成参数</h3>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-300">创造性 (Temperature)</span>
                      <span className="text-sm text-purple-400">{modelSettings.temperature.toFixed(2)}</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={modelSettings.temperature}
                      onChange={(e) => setModelSettings({ ...modelSettings, temperature: parseFloat(e.target.value) })}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">较低值使输出更确定，较高值使输出更随机创新</p>
                  </div>

                  <div>
                    <label className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-300">多样性 (Top P)</span>
                      <span className="text-sm text-purple-400">{modelSettings.topP.toFixed(2)}</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={modelSettings.topP}
                      onChange={(e) => setModelSettings({ ...modelSettings, topP: parseFloat(e.target.value) })}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">控制生成文本的多样性，建议保持在 0.8-1.0</p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleSaveModelSettings}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all"
              >
                <Save className="w-4 h-4" />
                保存参数设置
              </button>

              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-4 border border-purple-500/20">
                <div className="flex items-start gap-3">
                  <Sliders className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-1">参数说明</h4>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>• <strong>创造性</strong>：控制生成内容的随机性和创新性</li>
                      <li>• <strong>多样性</strong>：控制词汇选择的多样化程度</li>
                      <li>• 推荐设置：Temperature 0.8, Top P 0.9</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 底部操作栏 */}
        <div className="p-6 border-t border-gray-700/50 bg-gray-800/30">
          <button
            onClick={onClose}
            className="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-xl transition-all"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * 获取体验模型设置
 */
export const getExperienceModelSettings = () => {
  try {
    const saved = localStorage.getItem('experienceModelSettings')
    if (saved) {
      return JSON.parse(saved)
    }
  } catch (error) {
    console.error('读取设置失败:', error)
  }
  return {
    outputLength: 'medium',
    temperature: 0.8,
    topP: 0.9,
    memoryFrequency: 10,
    maxHistory: 20,
    autoSaveMemory: true
  }
}

